import { notification } from 'antd';
import { useCallback, useEffect, useMemo, useState, type Key, type ReactNode } from 'react';
import { generateUUID } from 'shared/helpers/utils/generate-uuid';
import {
  useGetEducationClassListQuery,
  useGetSubjectsQuery,
  useUpdateTeacherWorkloadMutation,
  type TeacherWorkload,
} from 'shared/api';
import type { TeacherWorkloadTableRow } from './types';
import { getColumns, ADD_SUBJECT_ROW_KEY, TEACHER_WORKLOAD_ADD_SUBJECT_ROW } from '../config';
import {
  areQualificationRowsEqualToWorkload,
  mapQualificationsToRows,
  rowsToQualificationRequests,
  validateRowsForSave,
} from './util';

export const useTeacherWorkloadTable = (
  teacherWorkload: TeacherWorkload,
  isQueryRefetching = false,
) => {
  const { data: educationClassesResponse } = useGetEducationClassListQuery({ size: 10000 });
  const { data: subjectsResponse, isFetching: subjectsLoading } = useGetSubjectsQuery({
    size: 10000,
    sort: ['name,asc'],
  });
  const [updateTeacherWorkload, { isLoading: isSaving }] = useUpdateTeacherWorkloadMutation();
  const [draftRows, setDraftRows] = useState<TeacherWorkloadTableRow[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [replaceClassesModalOpen, setReplaceClassesModalOpen] = useState(false);

  const qualificationsKey = useMemo(
    () => JSON.stringify(teacherWorkload.qualifications),
    [teacherWorkload.qualifications],
  );

  useEffect(() => {
    setDraftRows(mapQualificationsToRows(teacherWorkload));
  }, [teacherWorkload.id, qualificationsKey]);

  useEffect(() => {
    setSelectedRowKeys((keys) => keys.filter((k) => draftRows.some((r) => r.key === k)));
  }, [draftRows]);

  const isDirty = !areQualificationRowsEqualToWorkload(draftRows, teacherWorkload);

  const handleSave = useCallback(() => {
    const validationError = validateRowsForSave(draftRows);
    if (validationError) {
      notification.error({ message: validationError });
      return;
    }
    updateTeacherWorkload({
      id: teacherWorkload.id,
      qualifications: rowsToQualificationRequests(draftRows),
      workDays: teacherWorkload.workDays,
    }).unwrap();
  }, [draftRows, teacherWorkload.id, updateTeacherWorkload]);

  const handleSubjectChange = useCallback((rowKey: string, subjectId: number) => {
    setDraftRows((rows) =>
      rows.map((row) =>
        row.key === rowKey
          ? {
              ...row,
              subjectId,
            }
          : row,
      ),
    );
  }, []);

  const handleClassesChange = useCallback((rowKey: string, educationalClassIds: number[]) => {
    setDraftRows((rows) =>
      rows.map((row) =>
        row.key === rowKey
          ? {
              ...row,
              educationalClasses: educationalClassIds,
            }
          : row,
      ),
    );
  }, []);

  const handleDeleteRow = useCallback((rowKey: string) => {
    setDraftRows((prev) => {
      return prev.filter((row) => row.key !== rowKey);
    });
  }, []);

  const handleCopyRow = useCallback((rowKey: string) => {
    setDraftRows((rows) => {
      const index = rows.findIndex((row) => row.key === rowKey);
      if (index < 0) {
        return rows;
      }
      const source = rows[index];
      const copy: TeacherWorkloadTableRow = {
        key: generateUUID(),
        subjectId: source.subjectId,
        workloadHours: source.workloadHours,
        educationalClasses: [...source.educationalClasses],
      };
      return [...rows.slice(0, index + 1), copy, ...rows.slice(index + 1)];
    });
  }, []);

  const handleAddSubjectRow = useCallback(() => {
    setDraftRows((rows) => [
      ...rows,
      {
        key: generateUUID(),
        subjectId: null,
        workloadHours: 0,
        educationalClasses: [],
      },
    ]);
  }, []);

  const openReplaceClassesModal = useCallback(() => {
    setReplaceClassesModalOpen(true);
  }, []);

  const closeReplaceClassesModal = useCallback(() => {
    setReplaceClassesModalOpen(false);
  }, []);

  const applyReplaceClasses = useCallback(
    (educationalClassIds: number[]) => {
      if (educationalClassIds.length === 0) {
        notification.error({ message: 'Выберите хотя бы один класс' });
        return;
      }
      setDraftRows((rows) =>
        rows.map((row) =>
          selectedRowKeys.includes(row.key)
            ? { ...row, educationalClasses: [...educationalClassIds] }
            : row,
        ),
      );
      setSelectedRowKeys([]);
      setReplaceClassesModalOpen(false);
    },
    [selectedRowKeys],
  );

  const handleRowSelectionChange = useCallback((keys: Key[]) => {
    setSelectedRowKeys(keys);
  }, []);

  const selectedSubjectIds = draftRows
    .map((r) => r.subjectId)
    .filter((id): id is number => id != null);
  const educationClasses = educationClassesResponse?.content ?? [];
  const tableDisabled = isSaving || subjectsLoading;
  const canSave = isDirty && !tableDisabled;

  const tableColumns = getColumns({
    availableSubjects: subjectsResponse?.content ?? [],
    educationClasses,
    subjectsLoading,
    tableDisabled,
    selectedSubjectIds,
    onSubjectChange: handleSubjectChange,
    onClassesChange: handleClassesChange,
    onCopyRow: handleCopyRow,
    onDeleteRow: handleDeleteRow,
    onAddSubjectRow: handleAddSubjectRow,
  });

  const tableData = useMemo(() => [...draftRows, TEACHER_WORKLOAD_ADD_SUBJECT_ROW], [draftRows]);

  return {
    state: {
      tableData,
      tableColumns,
      tableLoading: isSaving || isQueryRefetching,
      isDirty,
      canSave,
      isSaving,
      educationClasses,
      selectedRowKeys,
      hasRowSelection: selectedRowKeys.length > 0,
      tableDisabled,
      rowSelection: {
        type: 'checkbox' as const,
        selectedRowKeys,
        onChange: (keys: Key[]) =>
          handleRowSelectionChange(keys.filter((k) => k !== ADD_SUBJECT_ROW_KEY)),
        renderCell: (
          _checked: boolean,
          record: TeacherWorkloadTableRow,
          _index: number,
          originNode: ReactNode,
        ) => (record.key === ADD_SUBJECT_ROW_KEY ? null : originNode),
        getCheckboxProps: (record: TeacherWorkloadTableRow) => ({
          disabled: tableDisabled || record.key === ADD_SUBJECT_ROW_KEY,
        }),
      },
      replaceClassesModalOpen,
    },
    methods: {
      onSave: handleSave,
      openReplaceClassesModal,
      closeReplaceClassesModal,
      applyReplaceClasses,
    },
  };
};
